import { useEffect, useMemo, useRef, useState } from "react";
import {
  buildBlocks,
  buildChordSegment,
} from "./responsiveChordSheetUtils";

const measureCharacterWidth = (fontSize) => {
  if (typeof document === "undefined") {
    return fontSize * 0.62;
  }

  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  context.font = `${fontSize}px Courier New`;
  return context.measureText("M").width || fontSize * 0.62;
};

const wrapLyricLine = (line, maxColumns) => {
  if (!line.trim()) {
    return [];
  }

  const segments = [];
  let start = 0;

  while (start < line.length) {
    const remaining = line.length - start;
    if (remaining <= maxColumns) {
      segments.push({
        start,
        text: line.slice(start),
        width: remaining,
      });
      break;
    }

    let breakIndex = start + maxColumns;
    while (breakIndex > start && line[breakIndex] !== " ") {
      breakIndex -= 1;
    }

    if (breakIndex === start) {
      breakIndex = start + maxColumns;
    }

    const segmentText = line.slice(start, breakIndex).trimEnd();
    segments.push({
      start,
      text: segmentText,
      width: breakIndex - start,
    });

    start = line[breakIndex] === " " ? breakIndex + 1 : breakIndex;
  }

  return segments;
};

const ResponsiveChordSheet = ({ tabContent, fontSize }) => {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    if (!containerRef.current || typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    observer.observe(containerRef.current);
    setContainerWidth(containerRef.current.clientWidth);

    return () => observer.disconnect();
  }, []);

  const blocks = useMemo(() => buildBlocks(tabContent), [tabContent]);
  const characterWidth = useMemo(() => measureCharacterWidth(fontSize), [fontSize]);
  const maxColumns = useMemo(() => {
    if (!containerWidth || !characterWidth) {
      return 32;
    }

    return Math.max(12, Math.floor(containerWidth / characterWidth) - 1);
  }, [characterWidth, containerWidth]);

  return (
    <div
      ref={containerRef}
      className="ResponsiveChordSheet"
      style={{ fontSize: `${fontSize}px` }}
    >
      {blocks.map((block) => {
        if (block.type === "spacer") {
          return <div key={block.id} className="GuitarTabLine-spacer" />;
        }

        if (block.type === "section") {
          return (
            <div key={block.id} className="GuitarTabLine GuitarTabLine-section">
              {block.text}
            </div>
          );
        }

        if (block.type === "pair") {
          const wrappedSegments = wrapLyricLine(block.lyrics, maxColumns);

          return (
            <div key={block.id} className="ResponsiveChordSheetPair">
              {wrappedSegments.map((segment, segmentIndex) => {
                const chordLine = buildChordSegment(segment, block.chords);
                return (
                  <div key={`${block.id}-${segmentIndex}`} className="ResponsiveChordSheetSegment">
                    <div className="GuitarTabLine GuitarTabLine-chords">{chordLine || " "}</div>
                    <div className="GuitarTabLine GuitarTabLine-lyrics">{segment.text}</div>
                  </div>
                );
              })}
            </div>
          );
        }

        return (
          <div key={block.id} className={`GuitarTabLine GuitarTabLine-${block.type}`}>
            {block.text}
          </div>
        );
      })}
    </div>
  );
};

export default ResponsiveChordSheet;
